import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { MaintenanceRecord } from '../utils/types';
import { getCustomerById } from './customerService';
import { sendNewWhatsAppMessage } from './whatsappService';
import { getSystemSettings } from './systemSettingsService';

/**
 * البحث عن الأجهزة المكتملة التي تحتاج لرسائل استعجال
 */
export const getDevicesNeedingUrgentMessages = async (): Promise<MaintenanceRecord[]> => {
  try {
    const maintenanceRef = collection(db, 'maintenance');
    
    // البحث عن الأجهزة المكتملة وغير المستلمة
    const q = query(
      maintenanceRef,
      where('status', '==', 'completed')
    );
    
    const querySnapshot = await getDocs(q);
    const records: MaintenanceRecord[] = [];
    
    const now = new Date();
    const twoDaysAgo = new Date(now.getTime() - (2 * 24 * 60 * 60 * 1000)); // قبل يومين
    const fortyEightHoursAgo = new Date(now.getTime() - (48 * 60 * 60 * 1000)); // قبل 48 ساعة
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const record: MaintenanceRecord = {
        id: doc.id,
        ...data,
        completedAt: data.completedAt?.toDate ? data.completedAt.toDate() : new Date(data.completedAt),
        lastUrgentMessageSentAt: data.lastUrgentMessageSentAt?.toDate ? 
          data.lastUrgentMessageSentAt.toDate() : 
          (data.lastUrgentMessageSentAt ? new Date(data.lastUrgentMessageSentAt) : undefined)
      } as MaintenanceRecord;
      
      // التحقق من أن الجهاز مكتمل منذ أكثر من يومين
      if (record.completedAt && new Date(record.completedAt) <= twoDaysAgo) {
        // التحقق من عدم إرسال رسالة استعجال خلال آخر 48 ساعة
        if (!record.lastUrgentMessageSentAt || 
            new Date(record.lastUrgentMessageSentAt) <= fortyEightHoursAgo) {
          records.push(record);
        }
      }
    });
    
    return records;
  } catch (error) {
    console.error('Error getting devices needing urgent messages:', error);
    throw error;
  }
};

/**
 * إرسال رسالة استعجال لعميل واحد
 */
export const sendUrgentMessageToCustomer = async (
  maintenanceRecord: MaintenanceRecord
): Promise<{ success: boolean; error?: string }> => {
  try {
    // الحصول على بيانات العميل
    const customer = await getCustomerById(maintenanceRecord.customerId);
    
    if (!customer || !customer.phone) {
      return { success: false, error: 'Customer not found or no phone number' };
    }
    
    // التحقق من إعدادات الواتساب
    const settings = await getSystemSettings();
    if (!settings.whatsappEnabled) {
      return { success: false, error: 'WhatsApp is disabled' };
    }
    
    // إرسال رسالة الاستعجال
    const result = await sendNewWhatsAppMessage('deviceUrgent', {
      customerName: customer.name,
      customerPhone: customer.phone,
      deviceType: maintenanceRecord.deviceType || 'غير محدد',
      deviceSize: maintenanceRecord.devices && maintenanceRecord.devices.length > 0 ? 
        maintenanceRecord.devices[0].size : '',
      orderId: maintenanceRecord.orderNumber ? 
        `#${maintenanceRecord.orderNumber}` : 
        maintenanceRecord.id.substring(0, 8)
    });
    
    if (result.success) {
      // تحديث تاريخ آخر رسالة استعجال
      await updateLastUrgentMessageDate(maintenanceRecord.id);
      console.log(`Urgent message sent to customer ${customer.name} for maintenance ${maintenanceRecord.id}`);
    }
    
    return result;
  } catch (error) {
    console.error('Error sending urgent message:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * تحديث تاريخ آخر رسالة استعجال
 */
export const updateLastUrgentMessageDate = async (maintenanceId: string): Promise<void> => {
  try {
    const maintenanceRef = doc(db, 'maintenance', maintenanceId);
    await updateDoc(maintenanceRef, {
      lastUrgentMessageSentAt: new Date()
    });
  } catch (error) {
    console.error('Error updating last urgent message date:', error);
    throw error;
  }
};

/**
 * إرسال رسائل الاستعجال لجميع الأجهزة المؤهلة
 */
export const sendAllUrgentMessages = async (): Promise<{
  totalProcessed: number;
  successCount: number;
  failureCount: number;
  errors: string[];
}> => {
  try {
    const devicesNeedingMessages = await getDevicesNeedingUrgentMessages();
    
    const results = {
      totalProcessed: devicesNeedingMessages.length,
      successCount: 0,
      failureCount: 0,
      errors: [] as string[]
    };
    
    console.log(`Found ${devicesNeedingMessages.length} devices needing urgent messages`);
    
    // إرسال الرسائل بشكل متتالي لتجنب الضغط على الخدمة
    for (const device of devicesNeedingMessages) {
      try {
        const result = await sendUrgentMessageToCustomer(device);
        
        if (result.success) {
          results.successCount++;
        } else {
          results.failureCount++;
          results.errors.push(`Device ${device.id}: ${result.error}`);
        }
        
        // انتظار قصير بين الرسائل
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        results.failureCount++;
        results.errors.push(`Device ${device.id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
    
    console.log(`Urgent messages processing completed: ${results.successCount} success, ${results.failureCount} failures`);
    
    return results;
  } catch (error) {
    console.error('Error sending all urgent messages:', error);
    throw error;
  }
};

/**
 * تشغيل خدمة رسائل الاستعجال (يتم استدعاؤها دورياً)
 */
export const runUrgentMessageService = async (): Promise<void> => {
  try {
    console.log('Starting urgent message service...');
    const results = await sendAllUrgentMessages();
    
    if (results.totalProcessed > 0) {
      console.log(`Urgent message service completed: ${results.successCount}/${results.totalProcessed} messages sent successfully`);
      
      if (results.errors.length > 0) {
        console.warn('Some urgent messages failed:', results.errors);
      }
    } else {
      console.log('No devices need urgent messages at this time');
    }
  } catch (error) {
    console.error('Error running urgent message service:', error);
  }
};
